import * as React from 'react';
import {isServer, view, createViewConfig, assignProps} from '@sail/engine';
import {getCurrencyDigits, message, useIntl} from '@sail/intl';
import type {View} from '../../view';

import {CompositeField} from '../CompositeField';
import type {CompositeFieldProps} from '../CompositeField';
import {TextField} from '../TextField';
import type {NativeTextField} from '../TextField';
import {FormFieldConfig, useFormFieldProps} from '../FormField';
import {css, external} from '../../css';
import {useNumericField} from '../../hooks/useNumericField/useNumericField';

import {
  ISO_CURRENCY_CODES,
  devWarn,
  createCurrencyFormatter,
  getCurrencySymbolAndPosition,
  getCurrencyNameFallback,
  matchesCurrencySearch,
  toDisplayValue,
  fromDisplayValue,
  getInitialNumericValue,
  getEffectiveControlledValue,
  buildCurrencySelectorData,
  getCurrencyFormattingMetadata,
  getEffectiveMinimumFractionDigits,
  stripCurrencySymbolFromFormattedValue,
  getValuePrecisionWarning,
} from './utils';
import {CurrencySelector} from './currency-selector/CurrencySelector';

export {ISO_CURRENCY_CODES, NON_ISO_CURRENCY_CODES} from './utils';

export type CurrencyFieldProps = Omit<
  View.IntrinsicElement<'fieldset'>,
  'onChange'
> &
  Pick<
    CompositeFieldProps,
    'disabled' | 'description' | 'error' | 'hiddenElements'
  > & {
    /**
     * An **integer** representing the amount of the currency, denominated in that
     * **currency's smallest unit**. e.g. $10.00 is 1000 in USD (or any other two-decimal currency) and ¥100 is 100 in JPY (a zero-decimal currency)
     */
    value?: number | null;

    /**
     * The value the component should start with. This allows initial control
     * if the component is going to be uncontrolled
     */
    defaultValue?: number;

    /**
     * The amount the component should format and display as a placeholder when
     * no value is present.
     * If you pass in a number, we will format the number using the currency's
     * default number of fractional digits (e.g. 2 for USD), but if you pass a
     * string, we will use the literal value of the string in all situations.
     *
     * @default 0
     */
    placeholderValue: number | string;

    /**
     * While typing, by default, we will not display any fractional digits until
     * the user types a decimal point. Turn this to true if you want to always
     * show the fractional digits, even if they are all zeros.
     * When this is false, we still always show the fractional digits once the
     * user has blurred the field.
     *
     * @default false
     */
    showEmptyFractionDigits: boolean;

    /**
     * By default, we will show the fractional digits of the placeholder value
     * that are inherent to the selected currency. For example, if the placeholderValue
     * is set to `0`, we will display `0.00` as the placeholder. If you'd prefer
     * the older behavior where we hide unused fractional digits, you can set this
     * to false.
     * Note: this only applies when the type of `placeholderValue` is a number.
     * All formatting is bypassed when `placeholderValue` is a string.
     *
     * @default true
     */
    showEmptyPlaceholderFractionDigits: boolean;

    /**
     * The currency that the value is denominated in
     */
    currency?: string;

    /**
     * The currency the component should start with. This allows initial control
     * if the component is going to be uncontrolled
     */
    defaultCurrency?: string;

    /**
     * Override the allowed "sub-cent" accuracy. By default we will use the currency's default
     * number of fractional digits, e.g. 2 for USD. This allows you to override that
     * behavior, by providing a number of additional digits to allow. This should typically
     * only be used in rare cases, and may have unintended localization consequences. You must
     * make sure that the data source you are integrating with expects this, since it
     * often means that the we no longer provide a whole integer.
     * You can also provide a negative value to reduce the default fractional accuracy.
     */
    fractionDigitOffset: number;

    /**
     * If you use the `fractionDigitOffset` prop, by default, we will only pad the
     * fraction digits that are expected by default. In USD, this would be padding 2
     * zeros. ($5 -> $5.00). This is means that users aren't visually prompted to add
     * more decimals if they don't have them, but they will be allowed to add them.
     * If `padAllFractionDigits` is true, we will pad all the digits.
     * (e.g. $5 -> $5.0000)
     */
    padAllFractionDigits: boolean;

    /**
     * By default we allow users to type negative values into the field. Set this
     * to false to disable that behavior.
     *
     * @default true
     */
    allowNegative: boolean;

    /**
     * Controls how the currency symbol is displayed. Passed directly to
     * Intl.NumberFormat's `currencyDisplay` option.
     *
     * - `'symbol'` (default): locale-dependent symbol (e.g. `US$` in some locales)
     * - `'narrowSymbol'`: compact symbol (e.g. always `$`)
     * - `'code'`: ISO code (e.g. `USD`)
     * - `'name'`: localized name (e.g. `US dollars`)
     */
    currencyDisplay?: Intl.NumberFormatOptions['currencyDisplay'];

    /**
     * The list of selectable currencies
     */
    currencyAllowlist: string[];

    /**
     * The callback function to fire when the value changes
     */
    onChange?: (value: number | null) => void;

    /**
     * The callback function to fire when the currency changes
     */
    onCurrencyChange?: (currency: string) => void;

    /**
     * The locale the component will use when formatting values. If not
     * provided, will default to the locale from the user's browser
     */
    locale?: string;

    /**
     * Text that describes the control. Will be both visible and clickable
     */
    label?: React.ReactNode;

    /**
     * Label for the currency selection dropdown select element. Not typically user visible.
     */
    currencySelectLabel?: string;

    /**
     * The size of the field
     */
    size: 'small' | 'medium' | 'large';

    /**
     * Makes the field non-editable
     */
    readOnly?: boolean;

    /**
     * Indicates whether the field is required for form submission
     */
    required?: boolean;

    /**
     * Controls whether the control should be automatically focused
     */
    autoFocus?: boolean;

    subviews?: View.Subviews<{
      /**
       * Customize the currency symbol node.
       */
      currencySymbol?: 'span';

      /**
       * Customize the value input node (the TextField wrapper).
       */
      valueInput?: typeof TextField;

      /**
       * Customize the inner input control of the value input.
       * Use this to apply styles directly to the `<input>` element,
       * e.g. `css: { textAlign: 'right' }`.
       */
      valueInputControl?: typeof NativeTextField;
    }>;
  };

type CurrencyMetaData = {
  symbol: string;
  symbolPosition: 'start' | 'end';
  denominationMultiple: number;
  fractionDigitMultiple: number;
  maximumFractionDigits: number;
  minimumFractionDigits: number;
};

const currencySymbolLabel = message({
  id: '@sail/ui.currencyField.currencySymbolLabel',
  description: 'The aria-label for the currency symbol in a currency field',
  defaultMessage: 'Currency symbol',
});

const valueInputLabel = message({
  id: '@sail/ui.currencyField.valueInputLabel',
  description: 'The label for the value input in a currency field',
  defaultMessage: 'Value',
});

const symbolSpanStyle = css({
  width: 'fit',
});

const SYMBOL_FONT_MAP = {
  small: css({font: 'label.small'}),
  medium: css({font: 'label.medium'}),
  large: css({font: 'label.large'}),
};

const valueInputProviderStyle = css({
  width: 'fill',
});

const valueInputEndMarginStyle = css({
  marginLeft: 'xsmall',
});

const inputPaddingResetRightStyle = css({
  paddingTop: 'space.0',
  paddingBottom: 'space.0',
  paddingRight: 'space.0',
});

const inputPaddingResetLeftStyle = css({
  paddingTop: 'space.0',
  paddingBottom: 'space.0',
  paddingLeft: 'space.0',
});

export const CurrencyFieldConfig = createViewConfig({
  props: {} as CurrencyFieldProps,
  name: 'CurrencyField',
  flattens: true,
  defaults: {
    allowNegative: true,
    currencyAllowlist: ISO_CURRENCY_CODES,
    disabled: false,
    fractionDigitOffset: 0,
    padAllFractionDigits: false,
    placeholderValue: 0,
    showEmptyFractionDigits: false,
    showEmptyPlaceholderFractionDigits: true,
    size: 'medium',
  },
});

/**
 * @param autoFocus
 * @param defaultValue
 * @param disabled
 * @param form
 * @param maxLength
 * @param minLength
 * @param name
 * @param onChange
 * @param onKeyPress
 * @param onKeyDown
 * @param onKeyUp
 * @param placeholder
 * @param readOnly
 * @param required
 * @param tabIndex
 */
export const CurrencyField = CurrencyFieldConfig.createView((props) => {
  const [fieldProps, controlProps] = useFormFieldProps({
    label: props.label,
    ...props,
  });

  const {disabled, error, label, ref: fieldRef, ...restFieldProps} = fieldProps;

  const {
    ref: controlRef,
    allowNegative,
    autoFocus,
    currency,
    currencyAllowlist,
    currencyDisplay,
    currencySelectLabel,
    defaultCurrency,
    defaultValue,
    fractionDigitOffset,
    locale,
    name,
    onChange,
    onCurrencyChange,
    padAllFractionDigits,
    placeholderValue,
    readOnly,
    required,
    showEmptyFractionDigits,
    showEmptyPlaceholderFractionDigits,
    size,
    value,
    ...restControlProps
  } = controlProps;

  const restProps = {...restControlProps, ...restFieldProps};

  const intl = useIntl();

  const valueIsControlledRef = React.useRef<boolean>(
    typeof value !== 'undefined',
  );
  const valueIsControlled = valueIsControlledRef.current;
  const currencyIsControlledRef = React.useRef<boolean>(
    typeof currency !== 'undefined',
  );
  const compositeFieldRef = React.useRef<HTMLDivElement>(null);

  const internalLocale =
    locale ?? (isServer ? 'en-US' : navigator?.language ?? 'en-US');

  const doesSupportDecimals = fractionDigitOffset > 0;

  let initialNumericValue = getInitialNumericValue(
    fractionDigitOffset,
    defaultValue,
    value,
  );

  // Don't allow negative default or controlled values if allowNegative is false
  if (!allowNegative) {
    if (typeof defaultValue === 'number' && defaultValue < 0) {
      initialNumericValue = undefined;
      devWarn(
        'CurrencyField was provided a negative defaultValue while allowNegative was set to false. Ignoring defaultValue.',
      );
    } else if (typeof value === 'number' && value < 0) {
      initialNumericValue = undefined;
      devWarn(
        'CurrencyField was provided a negative value while allowNegative was set to false. Ignoring value.',
      );
    }
  }

  const [internalCurrency, setInternalCurrency] = React.useState<string>(
    currency ?? defaultCurrency ?? currencyAllowlist[0] ?? 'USD',
  );
  // If we have ever displayed the digits from the fractionDigitOffset
  // it's jarring to take them away. So we track the state, and once we
  // show them, we always show them.
  const [hasShownFractionOffset, setHasShownFractionOffset] =
    React.useState<boolean>(padAllFractionDigits);
  // Sticky fractional mode: once fractions have been shown (triggered by a
  // non-integer controlled value or decimal input), they stay visible until
  // the field is cleared.
  const [fractionalMode, setFractionalMode] = React.useState(false);

  const {
    symbol,
    symbolPosition,
    denominationMultiple,
    fractionDigitMultiple,
    minimumFractionDigits,
    maximumFractionDigits,
  }: CurrencyMetaData = React.useMemo(() => {
    const currencyDigits = getCurrencyDigits(internalCurrency);

    const {symbol, symbolPosition} = getCurrencySymbolAndPosition(
      internalLocale,
      internalCurrency,
      currencyDisplay,
    );

    return {
      symbol,
      symbolPosition,
      ...getCurrencyFormattingMetadata(currencyDigits, fractionDigitOffset),
    };
  }, [internalCurrency, internalLocale, fractionDigitOffset, currencyDisplay]);

  // Determine the effective controlled value, filtering out invalid precision.
  // null = controlled-but-empty or invalid precision, undefined = uncontrolled.
  const effectiveControlledValue = React.useMemo(
    () =>
      getEffectiveControlledValue(
        valueIsControlled,
        value,
        doesSupportDecimals,
      ),
    [value, valueIsControlled, doesSupportDecimals],
  );

  const currencyData = React.useMemo(
    () => buildCurrencySelectorData(currencyAllowlist, internalCurrency),
    [currencyAllowlist, internalCurrency],
  );

  const getLocalizedCurrencyName = React.useCallback(
    (code: string): string => {
      const localizedName = intl.formatDisplayName(code, {type: 'currency'});

      // Fall back to the English name from @stripe-internal/currencies for codes not in CLDR (e.g. crypto).
      return localizedName || getCurrencyNameFallback(code) || code;
    },
    [intl],
  );

  const matchesCurrencyFilter = React.useCallback(
    <T,>(textValue: string, inputValue: string, item: T): boolean => {
      const code = (item as {id: string}).id;
      const localizedName = getLocalizedCurrencyName(code);
      const englishName = getCurrencyNameFallback(code.toLowerCase());

      return matchesCurrencySearch(inputValue, [
        textValue,
        localizedName,
        englishName !== localizedName ? englishName : null,
      ]);
    },
    [getLocalizedCurrencyName],
  );

  const handleToDisplayValue = React.useCallback(
    (normalizedValue: number, _fractionalDigits: number) =>
      toDisplayValue(normalizedValue, denominationMultiple),
    [denominationMultiple],
  );

  const handleFromDisplayValue = React.useCallback(
    (displayValue: number, _fractionalDigits: number) =>
      fromDisplayValue(
        displayValue,
        denominationMultiple,
        fractionDigitMultiple,
      ),
    [denominationMultiple, fractionDigitMultiple],
  );

  const handleHookChange = React.useCallback(
    (nextValue?: number) => {
      if (nextValue === undefined) {
        setFractionalMode(false);
      }
      onChange?.(nextValue ?? null);
    },
    [onChange],
  );

  const handleExtendedFractionShown = React.useCallback(
    () => setHasShownFractionOffset(true),
    [],
  );

  const handleBlur: typeof restProps.onBlur = (e) => {
    if (restProps.onBlur) {
      restProps.onBlur(e);
    }
  };

  const handleSelectChange = (key: string | number): void => {
    if (typeof key === 'string') {
      if (!currencyIsControlledRef.current) {
        setInternalCurrency(key);
      }
      onCurrencyChange?.(key);
    }
  };

  // `useNumericField` is itself a data-returning hook, but it must be called
  // here (rather than grouped with the other hooks above) because it depends
  // on the memoized formatting metadata and the display-value callbacks.
  const {displayValue, numericValue, inputRef, onKeyDown, onInput} =
    useNumericField({
      value: effectiveControlledValue,
      defaultValue: initialNumericValue,
      onChange: handleHookChange,
      locale: internalLocale,
      fractionalDigits: maximumFractionDigits,
      minimumFractionDigits: getEffectiveMinimumFractionDigits(
        hasShownFractionOffset,
        denominationMultiple,
        minimumFractionDigits,
        maximumFractionDigits,
      ),
      forceMaximumFractionDigits:
        hasShownFractionOffset || padAllFractionDigits,
      autoFocus,
      allowNegative,
      toDisplayValue: handleToDisplayValue,
      fromDisplayValue: handleFromDisplayValue,
      showEmptyFractionDigits: showEmptyFractionDigits || fractionalMode,
      onExtendedFractionDigitsShown: handleExtendedFractionShown,
      onUnsafeValue: fractionDigitOffset < 0 ? handleHookChange : undefined,
      padFractionsOnBlur: !(disabled || readOnly),
      suppressGroupSeparatorAsDecimal: true,
      disableControlledRejection: true,
    });

  // Memoizations that depend on useNumericField's output.
  const renderedSymbol = React.useMemo(() => {
    if (currencyDisplay !== 'name') {
      return symbol;
    }

    let valueToFormat = 0;
    if (numericValue != null) {
      valueToFormat = toDisplayValue(numericValue, denominationMultiple);
    } else if (typeof placeholderValue === 'number') {
      valueToFormat = placeholderValue / denominationMultiple;
    }

    const formattedParts = createCurrencyFormatter(
      internalLocale,
      internalCurrency,
      {currencyDisplay},
    ).formatToParts(Math.abs(valueToFormat));
    const currencyPart = formattedParts.find(
      (part) => part.type === 'currency',
    );

    return currencyPart?.value ?? symbol;
  }, [
    currencyDisplay,
    symbol,
    numericValue,
    placeholderValue,
    denominationMultiple,
    internalLocale,
    internalCurrency,
  ]);

  const placeholderFormatter = React.useMemo(
    () =>
      createCurrencyFormatter(internalLocale, internalCurrency, {
        currencyDisplay,
        ...(showEmptyPlaceholderFractionDigits
          ? {
              maximumFractionDigits,
              minimumFractionDigits: getEffectiveMinimumFractionDigits(
                hasShownFractionOffset,
                denominationMultiple,
                minimumFractionDigits,
                maximumFractionDigits,
              ),
            }
          : {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
      }),
    [
      internalCurrency,
      internalLocale,
      currencyDisplay,
      maximumFractionDigits,
      minimumFractionDigits,
      hasShownFractionOffset,
      denominationMultiple,
      showEmptyPlaceholderFractionDigits,
    ],
  );

  // Callback that depends on the placeholderFormatter memoization above.
  const formatCurrencyPlaceholder = React.useCallback(
    (placeholderNum: number): string => {
      const valueToFormat = placeholderNum / denominationMultiple;
      return stripCurrencySymbolFromFormattedValue(
        placeholderFormatter.format(valueToFormat),
        symbol,
      );
    },
    [placeholderFormatter, symbol, denominationMultiple],
  );

  // sync currency prop changes to state, with error handling
  React.useEffect(() => {
    if (typeof currency === 'string') {
      if (currencyIsControlledRef.current) {
        setInternalCurrency(currency);
      } else {
        devWarn(
          'You attempted to set `currency` after initializing `CurrencyField` as uncontrolled. This change will be ignored.',
        );
      }
    }
  }, [currency]);

  React.useEffect(() => {
    if (
      !fractionalMode &&
      effectiveControlledValue != null &&
      effectiveControlledValue % denominationMultiple !== 0
    ) {
      setFractionalMode(true);
    }
  }, [fractionalMode, effectiveControlledValue, denominationMultiple]);

  // Attach onKeyDown directly on the native <input> because TextField inside
  // CompositeField doesn't forward the onKeyDown prop.
  const onKeyDownRef = React.useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;

  React.useEffect(() => {
    let el: HTMLInputElement | null = null;
    const refEl = inputRef.current;
    if (refEl && (refEl as unknown as HTMLElement).tagName === 'INPUT') {
      el = refEl;
    } else if (refEl) {
      el = (refEl as unknown as HTMLElement).querySelector?.(
        'input[type="text"]',
      ) as HTMLInputElement | null;
    }
    if (!el) {
      const root = compositeFieldRef.current;
      el = root?.querySelector('input[type="text"]') as HTMLInputElement | null;
    }
    if (!el) {
      return;
    }

    const handler = (e: Event) => {
      onKeyDownRef.current(
        e as unknown as React.KeyboardEvent<HTMLInputElement>,
      );
    };
    el.addEventListener('keydown', handler);

    return () => {
      el!.removeEventListener('keydown', handler);
    };
  }, [inputRef]);

  // Validate controlled value and warn about precision issues
  React.useEffect(() => {
    if (!valueIsControlledRef.current && value !== undefined) {
      devWarn(
        'You attempted to set `value` after initializing `CurrencyField` as uncontrolled. This change will be ignored.',
      );
      return;
    }
    const warning = getValuePrecisionWarning(
      value,
      doesSupportDecimals,
      fractionDigitOffset,
      internalCurrency,
    );
    if (warning) {
      devWarn(warning);
    }
  }, [value, doesSupportDecimals, fractionDigitOffset, internalCurrency]);

  const hiddenValue =
    valueIsControlled && effectiveControlledValue !== undefined
      ? effectiveControlledValue
      : numericValue;

  const symbolSpan = (
    <view.span
      aria-label={intl.formatMessage(currencySymbolLabel)}
      css={[symbolSpanStyle, SYMBOL_FONT_MAP[size]]}
      inherits={props.subviews.currencySymbol}
    >
      {renderedSymbol}
    </view.span>
  );

  const shouldShowCurrencySelector =
    currencyData.items.length >
    (!currencyData.items.some((item) => item.id === internalCurrency) ? 0 : 1);

  const currencySelector = shouldShowCurrencySelector ? (
    <CurrencySelector
      compositeFieldRef={compositeFieldRef}
      currencyData={currencyData}
      currencySelectLabel={currencySelectLabel}
      defaultFilter={matchesCurrencyFilter}
      disabled={disabled}
      getItemDescription={getLocalizedCurrencyName}
      hasError={Boolean(error)}
      internalCurrency={internalCurrency}
      onSelectionChange={handleSelectChange}
      readOnly={readOnly}
      size={size}
      symbolPosition={symbolPosition}
    />
  ) : null;

  const inputPaddingReset =
    symbolPosition !== 'end'
      ? inputPaddingResetRightStyle
      : inputPaddingResetLeftStyle;
  const valueInputMargin =
    symbolPosition === 'end' ? valueInputEndMarginStyle : undefined;

  return (
    <CompositeField
      {...restProps}
      ref={fieldRef ?? controlRef}
      inherits={props.inherits}
      label={label}
      disabled={disabled}
      size={size}
      onBlur={handleBlur}
      error={error}
      subviews={{
        inputs: {
          uses: [
            assignProps({
              ref: compositeFieldRef,
            }),
          ],
          css: shouldShowCurrencySelector
            ? external(inputPaddingReset)
            : undefined,
        },
      }}
    >
      {symbolPosition === 'start' ? symbolSpan : currencySelector}

      <FormFieldConfig.Provider value={{css: valueInputProviderStyle}}>
        <TextField
          ref={inputRef}
          inherits={props.subviews.valueInput}
          subviews={{input: props.subviews.valueInputControl}}
          css={valueInputMargin}
          onChange={onInput}
          placeholder={
            typeof placeholderValue === 'string'
              ? placeholderValue
              : formatCurrencyPlaceholder(placeholderValue)
          }
          inputMode={maximumFractionDigits > 0 ? 'decimal' : 'numeric'}
          value={displayValue}
          readOnly={readOnly}
          autoFocus={autoFocus}
          aria-required={required}
          label={intl.formatMessage(valueInputLabel)}
        />
      </FormFieldConfig.Provider>

      {symbolPosition === 'end' ? symbolSpan : currencySelector}

      <view.input
        type="hidden"
        name={name}
        value={hiddenValue ?? ''}
        disabled={disabled}
        required={required}
      />
    </CompositeField>
  );
});
